import React from 'react';
import { ITableDataSource } from 'models/table';
import { colors } from 'theme/colors';
import Grid from '@mui/material/Grid';
import { Typography } from '@mui/material';
import Button from 'components/atoms/Button';
import { EButtonVariants } from 'constants/Buttons';
import { useTranslations } from 'hooks/useTranslations';
import { formatDate } from 'utils/date';
import { useGlobalProperty } from 'models/globalPropertyContext';
import { EPermission } from 'models/permissions';
import { emptyFieldHolder } from 'components/molecules/CardList';

export enum EAmmunitionTransactionsLabel {
  transactionDateAndTime = 'ammunitionDetails.transactions.DateAndTime.column',
  createDate = 'ammunitionDetails.transactions.createDate.column',
  transactionType = 'ammunitionDetails.transactions.transactionType.column',
  owner = 'ammunitionDetails.transactions.owner.column',
  keeper = 'ammunitionDetails.transactions.keeper.column',
}

interface IAmmunitionTransactionsCard {
  data: ITableDataSource;
  discardableTransactionId?: string | null;
  handleView: (args: string) => void;
  handleDelete: (transactionId: string) => void;
}

const AmmunitionTransactionsCard = ({
  data,
  handleView,
  discardableTransactionId,
  handleDelete,
}: IAmmunitionTransactionsCard) => {
  const { t } = useTranslations();
  const { longDateFormat } = useGlobalProperty();
  return (
    <Grid
      container
      sx={{
        border: colors.gray200,
        borderRadius: '4px',
        overflow: 'hidden',
        mt: 1,
        backgroundColor: colors.gray100,
      }}
    >
      <Grid size={{ xs: 12, sm: 6, md: 4, lg: 2 }} sx={{ p: 2 }}>
        <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
          {t(EAmmunitionTransactionsLabel.transactionType)}
        </Typography>
        <Typography variant="body2">{data.transactionType}</Typography>
      </Grid>
      <Grid size={{ xs: 12, sm: 6, md: 4, lg: 2 }} sx={{ p: 2 }}>
        <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
          {t(EAmmunitionTransactionsLabel.transactionDateAndTime)}
        </Typography>
        <Typography variant="body2">{formatDate(data.date, longDateFormat)}</Typography>
      </Grid>
      <Grid size={{ xs: 12, sm: 6, md: 4, lg: 2 }} sx={{ p: 2 }}>
        <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
          {t(EAmmunitionTransactionsLabel.createDate)}
        </Typography>
        <Typography variant="body2">{formatDate(data.createdDate, longDateFormat)}</Typography>
      </Grid>
      <Grid size={{ xs: 12, sm: 6, md: 4, lg: 2 }} sx={{ p: 2 }}>
        <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
          {t(EAmmunitionTransactionsLabel.owner)}
        </Typography>
        <Typography variant="body2">{emptyFieldHolder(data.owner)}</Typography>
      </Grid>
      <Grid size={{ xs: 12, sm: 6, md: 4, lg: 2 }} sx={{ p: 2 }}>
        <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
          {t(EAmmunitionTransactionsLabel.keeper)}
        </Typography>
        <Typography variant="body2">{emptyFieldHolder(data.keeper)}</Typography>
      </Grid>

      <Grid
        container
        size={{ xs: 12, lg: 2 }}
        sx={{
          justifyContent: 'space-between',
          alignItems: 'center',
          borderTop: {
            xs: `1px solid ${colors.primary100}`,
            lg: 'none',
          },
          p: 2,
          gap: 1,
        }}
      >
        <Grid size={{ xs: 12, sm: 3 }} sx={{ mb: { xs: 2, sm: 0 } }}>
          <Typography variant="button" sx={{ textTransform: 'uppercase' }}>
            {t('general.actions.button')}:{' '}
          </Typography>
        </Grid>

        {data.detailsVisible && (
          <Grid size={{ xs: 12, sm: 6 }}>
            <Button
              label={t('ammunitionDetails.transactions.detail.button')}
              variant={EButtonVariants.outlined}
              onClick={() => handleView(data.id as string)}
              size="small"
              fullWidth
            />
          </Grid>
        )}
        {discardableTransactionId === (data.id as string) && (
          <Grid size={{ xs: 12, sm: 3 }}>
            <Button
              label={t(
                'registeredFirearms.firearmsDetails.transactions.accordion.remove.link',
              )}
              variant={EButtonVariants.outlined}
              permission={EPermission.DISCARD_TRANSACTION}
              onClick={() => handleDelete(data.id as string)}
              size="small"
              fullWidth
            />
          </Grid>
        )}
      </Grid>
    </Grid>
  );
};

export { AmmunitionTransactionsCard };
